/**
 * transport/stt.ts —— **语音转写面客户端**（★FE-E3 账行 `L-PF-ASR-1`／`L-PF-ASR-2`）。
 *
 * 由来：后端 `POST /api/stt`（`api/stt_face.py`）与出网白名单（`whitelist.ts::EGRESS_PATHS.stt`）
 * 早已在位（★FE2 `L-FE2-3`）。**本件＝那条实装线**：`Composer` 语音位缺省注入本件
 * （`shell/Composer.tsx:195` `sttApi: props.sttApi ?? sttTransport`）⇒ 生产路径录音后真转写；
 * `useComposerVoice`（`composables/useComposerVoice.ts:143`）的 `sttApi` 为空分支只在注入面被
 * **显式置空**时可达（单测替身），且该支本身是显式 notify（不静默）。
 *
 * 载体形态（`L-PF-ASR-2` 定案 · 20261012）：**JSON 单形** `{audio_base64, filename}`
 *   —— 依据＝本树入口执行层既有范式：`runtime/ingress_server.py:317` `body = _decode_json_body(raw,
 *   Content-Type)`；`_decode_json_body:47-49`「Content-Type 非 `application/json` ⇒ 体 `None`」
 *   ⇒ 本入口**只认 JSON 单形**（旧面 102 `app/api/stt.py:54` 的 multipart **不采纳**：
 *   支持它＝改入口执行层，越出本行工作面）。定案值逐字同 `api/stt_face.py::AUDIO_CARRIER`
 *   （`"json+base64"`）——**前后端同形**；异形 ⇒ 服务端显式 400（不静默）。
 *
 * 纪律（同 `models.ts`／`files.ts`）：路径经白名单（`assertEgress("stt", "/api/stt")`，逐字）；
 *   浏览器零凭据（同源 cookie 自动携带）；失败**显式**（`ok:false` ＋ `errorText`），不静默假成功。
 *   ⚠️ 消费面契约：`useComposerVoice` 的 `sttApi` 回 `SttResult{ok,text?,errorText?}`（**返回值**形态，
 *   非抛）⇒ 本件按该契约回结果；唯**白名单越界**（编程错误）仍如实抛（`TransportError`）。
 *
 * 文案（★A-044①）：用户可见话术只来自 `errorText.ts::hintFor`（本件只传**数据位** detail：
 *   `"short"`／`"absent"`／`""`）。旧数字码同义映射：旧 40001「音频太短」⇒ HTTP **400**；
 *   旧 50006「服务不可用」⇒ HTTP **503**（`api/stt_face.py::STATUS_AUDIO_SHORT/STATUS_ENGINE_ABSENT`）。
 */
import { assertEgress } from "./whitelist";
import type { FetchLike } from "./session";
import { E_CODES, hintFor } from "./errorText";

/** 载体形态定案值（逐字同 `api/stt_face.py::AUDIO_CARRIER`；前后端同形锚） */
export const STT_AUDIO_CARRIER = "json+base64";
/** 音频载荷字段（逐字同 `api/stt_face.py::AUDIO_FIELD`） */
export const STT_AUDIO_FIELD = "audio_base64";
/** 可选文件名字段（后缀透传引擎；逐字同 `api/stt_face.py::FILENAME_FIELD`） */
export const STT_FILENAME_FIELD = "filename";
export const STT_DEFAULT_FILENAME = "audio.wav";
/** 旧 50006「语音识别服务不可用」同义状态（引擎不可达／引擎错） */
export const STT_STATUS_ENGINE_ABSENT = 503;
/** 旧 40001「音频太短」同义状态（空载荷／缺字段／体非法／非合法 base64） */
export const STT_STATUS_AUDIO_REJECTED = 400;

/** 旧数字码同义映射（册 §6.6 显式码口径）：旧 50006「服务不可用」／旧 40001「音频太短」 */
export const STT_CODE_ENGINE_ABSENT = "50006";
export const STT_CODE_AUDIO_SHORT = "40001";

/** 状态 ⇒ 旧同义数字码（单源；`sttApi` 回包与消费面 notify 同用此映射） */
export function legacyCodeOf(status: number): string | null {
  if (status === STT_STATUS_ENGINE_ABSENT) return STT_CODE_ENGINE_ABSENT;
  if (status === STT_STATUS_AUDIO_REJECTED) return STT_CODE_AUDIO_SHORT;
  return null;
}

/** 文案数据位（`hintFor` 的分流键；话术只在本仓 `transport/errorText.ts`） */
const DETAIL_SHORT = "short";
const DETAIL_ABSENT = "absent";

/** 消费面结果（**超集** of `useComposerVoice::SttResult`：`ok`／`text`／`errorText` 同名同义） */
export interface SttOutcome {
  ok: boolean;
  /** HTTP 状态（0＝请求未达服务，如网络失败/环境缺 base64 能力） */
  status: number;
  text?: string;
  lowConfidence?: boolean;
  /** 服务端错误码（数据位；未给 ⇒ null） */
  code?: string | null;
  errorText?: string;
}

/** FileReader 兜底（环境无 `Blob.arrayBuffer`——如 jsdom 旧版／老浏览器） */
function readAsDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const Reader = (globalThis as { FileReader?: typeof FileReader }).FileReader;
    if (!Reader) { reject(new Error("no FileReader")); return; }
    const reader = new Reader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}

/** Blob ⇒ base64（分块拼接防 `String.fromCharCode` 参数上限；环境无 `btoa` ⇒ 显式失败，不静默） */
export async function blobToBase64(blob: Blob): Promise<string> {
  if (typeof globalThis.btoa !== "function") throw new Error("no btoa");
  if (typeof blob.arrayBuffer !== "function") {
    // 兜底：data URL 的 `,` 之后即 base64 载荷（同一编码，字节等价）
    const dataUrl = await readAsDataURL(blob);
    const comma = dataUrl.indexOf(",");
    if (comma < 0) throw new Error("bad data url");
    return dataUrl.slice(comma + 1);
  }
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return globalThis.btoa(binary);
}

/** 请求体序列化（JSON 单形；唯一构造点——判据「前后端同形」的观测位） */
export function sttRequestBody(base64: string, filename: string = STT_DEFAULT_FILENAME): string {
  return JSON.stringify({ [STT_AUDIO_FIELD]: base64, [STT_FILENAME_FIELD]: filename });
}

/** 转写回包体（`api/stt_face.py` 转发 `stt_client.transcribe_file` 产物；只读本件用到的键） */
interface SttDoc {
  state?: string;
  text?: string | null;
  low_confidence?: boolean;
}

function fail(status: number, code: string | null, detail: string): SttOutcome {
  // 显式码（册 §6.6）：状态码 → 旧同义数字码（503⇒50006／400⇒40001）；服务端带码优先用服务端码
  return { ok: false, status, code: code ?? legacyCodeOf(status), errorText: hintFor(E_CODES.STT_FAILED, detail) };
}

/** 失败体 ⇒ 服务端错误码（数据位；体非 JSON ⇒ null；不抛——失败由状态码承载） */
async function readFailureCode(res: { json: () => Promise<unknown> }): Promise<string | null> {
  try {
    const body = (await res.json()) as { error?: string } | null;
    return body && body.error ? String(body.error) : null;
  } catch {
    return null;
  }
}

/**
 * 录音 Blob ⇒ 转写文本（= `useComposerVoice` 的 `sttApi` 实装）。
 *
 * 返回：转成 ⇒ `{ok:true, text}`；引擎在位但空转写 ⇒ `{ok:true, text:""}`（消费面按「未能识别」显式提示，
 * **不编造文本**）；音频太短／体非法 ⇒ `{ok:false, status:400}`；引擎不可达／引擎错 ⇒ `{ok:false, status:503}`。
 * 白名单越界（`path !== "/api/stt"`）⇒ 抛 `TransportError`（编程错误不吞）。
 */
export async function transcribeAudio(
  blob: Blob,
  fetchFn: FetchLike = fetch,
  filename: string = STT_DEFAULT_FILENAME,
): Promise<SttOutcome> {
  const path = assertEgress("stt", "/api/stt");
  let base64: string;
  try {
    base64 = await blobToBase64(blob);
  } catch {
    return fail(0, null, DETAIL_SHORT);            // 环境无 base64 能力／blob 不可读 ⇒ 显式（不静默、不假装成功）
  }
  if (!base64) return fail(0, null, DETAIL_SHORT); // 空载荷：本地即拒（同服务端 E_STT_EMPTY 语义，不空转网络）
  let res: { ok: boolean; status: number; json: () => Promise<unknown> };
  try {
    res = (await fetchFn(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },   // ★JSON 单形（L-PF-ASR-2 定案；异形服务端 400）
      body: sttRequestBody(base64, filename),
    })) as unknown as { ok: boolean; status: number; json: () => Promise<unknown> };
  } catch {
    return fail(0, null, "");                       // 网络层失败（未达服务）⇒ 显式失败文案
  }
  if (!res.ok) {
    const code = await readFailureCode(res);
    const detail = res.status === STT_STATUS_ENGINE_ABSENT ? DETAIL_ABSENT
      : res.status === STT_STATUS_AUDIO_REJECTED ? DETAIL_SHORT : "";
    return fail(res.status, code, detail);
  }
  let doc: SttDoc | null = null;
  try {
    doc = (await res.json()) as SttDoc | null;
  } catch {
    return fail(res.status, null, "");              // 200 但体非 JSON ⇒ 显式失败（不伪造文本）
  }
  const text = doc && typeof doc.text === "string" ? doc.text : "";
  if (!text.trim()) return { ok: true, status: res.status, text: "" };   // 空转写：如实缺位（消费面显式提示）
  return { ok: true, status: res.status, text, lowConfidence: doc?.low_confidence === true };
}

/** 缺省实装（`Composer` 语音位缺省注入用；形状＝`useComposerVoice` 的 `sttApi`） */
export const sttApi = transcribeAudio;
export default transcribeAudio;
